/**
 * RELATÓRIO DA TURMA — onde os alunos erram, de que jeito, e quanto cada item de fato
 * custou.
 *
 * Os dados chegam de dois jeitos: o histórico deste navegador e os CÓDIGOS DE
 * RESULTADO que cada aluno copia no fim do percurso e manda ao professor (ver
 * `resultados.ts`).
 *
 * Três leituras:
 *  1. Por item: respostas, acerto, dicas por resposta e os erros mais comuns.
 *  2. Mapa de calor item × erro nomeado: a turma erra ONDE, e de que jeito. É a leitura
 *     que só existe porque cada erro tem nome.
 *  3. Calibração: com respostas suficientes, o b de cada item estimado pelo modelo de
 *     Rasch, ao lado do b̂ a priori — e a opção de levar esse b ao mapa.
 */

import { formatBR } from '../core/num.ts';
import { conta, numeroDe, verbo } from '../core/pt.ts';
import { seedSequence } from '../core/rng.ts';
import { calibrarRasch, type ItemCalibrado } from '../engine/calibracao.ts';
import { generate } from '../engine/generate.ts';
import { getTemplate, hasTemplate } from '../engine/registry.ts';
import { ERROR_CATALOG } from '../schema/errors.ts';
import { salvarCalibracao } from './calibracaoSalva.ts';
import { FICHAS } from './habilidades.ts';
import { carregarHistorico, type Tentativa } from './historico.ts';
import { lerCodigos } from './resultados.ts';
import { DEMO } from './demo.ts';
import { TURMA_DE_EXEMPLO } from './demoTurma.ts';
import type { ContextoApp, Vista } from './tipos.ts';

export const MIN_ALUNOS_CALIBRACAO = 5;
export const MIN_RESPOSTAS_ITEM = 5;

const bCache = new Map<string, number>();

/**
 * DIFICULDADE EM PALAVRAS.
 *
 * b̂ é um logit, e logit não é unidade de nenhum professor. Mas apagá-lo seria pior:
 * é o número que torna a escala conferível, e é o que uma banca vem ver. Então ele
 * fica — ao lado, em mono, e no `title` de cada linha — e a leitura principal passa a
 * ser uma faixa em palavras.
 *
 * O corte é o do aluno mediano da escala (θ = 0): abaixo de 0 ele acerta mais que
 * erra; acima de 2 ele acerta menos de uma em oito.
 */
const FAIXAS: readonly { readonly ate: number; readonly rotulo: string }[] = [
  { ate: 0, rotulo: 'Baixa' },
  { ate: 1, rotulo: 'Média' },
  { ate: 2, rotulo: 'Alta' },
  { ate: Infinity, rotulo: 'Muito alta' },
];

const faixaDeB = (b: number): string =>
  (FAIXAS.find((x) => b < x.ate) as { rotulo: string }).rotulo;

/**
 * "Quantos em dez" — a chance de acerto do aluno mediano DESTA turma, pelo modelo de
 * Rasch: P = 1/(1+e^(b−θ̄)). É a mesma conta que a calibração usa, então o professor
 * pode conferir a tradução na própria tabela: "5 em 10" tem de cair sobre os 50%
 * observados na coluna de acerto.
 */
const emDez = (b: number, thetaBarra: number): number =>
  Math.round(10 / (1 + Math.exp(b - thetaBarra)));

/** A mediana das proficiências estimadas: o aluno do meio desta turma. */
function thetaMediano(pessoas: ReadonlyMap<string, number>): number {
  const v = [...pessoas.values()].sort((a, b) => a - b);
  if (v.length === 0) return 0;
  const m = Math.floor(v.length / 2);
  return v.length % 2 === 1 ? (v[m] as number) : ((v[m - 1] as number) + (v[m] as number)) / 2;
}

/** O b̂ a priori do item-modelo: a média entre algumas variantes. */
function bAPriori(templateId: string): number {
  const c = bCache.get(templateId);
  if (c !== undefined) return c;
  const bs: number[] = [];
  for (const s of seedSequence(`rel-${templateId}`, 0, 8)) {
    const r = generate(getTemplate(templateId), s);
    if (r.status === 'ok') bs.push(r.instance.difficulty.provisionalB);
  }
  const m = bs.length > 0 ? bs.reduce((a, b) => a + b, 0) / bs.length : 0;
  bCache.set(templateId, m);
  return m;
}

const ORDEM = new Map(FICHAS.map((f, i) => [f.templateId, i]));
const nomeCurto = (id: string): string => FICHAS.find((f) => f.templateId === id)?.nomeCurto ?? id;
const pct = (a: number, n: number): string => (n === 0 ? '—' : `${Math.round((100 * a) / n)}%`);
const rotuloErro = (codigo: string): string => ERROR_CATALOG[codigo]?.label ?? codigo;
/** Um logit: duas casas, e o sinal de menos quando há. */
const logit = (v: number): string => formatBR(v, 2);

interface Linha {
  readonly pessoa: string;
  readonly t: Tentativa;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  texto?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (texto !== undefined) e.textContent = texto;
  return e;
}

function tabela(
  cabecalho: readonly string[],
  linhas: readonly (readonly (string | HTMLElement)[])[],
  cls = 'rel-tabela',
): HTMLElement {
  const box = el('div', 'rel-rolagem');
  const t = el('table', cls);
  const tr = el('tr');
  for (const c of cabecalho) tr.appendChild(el('th', undefined, c));
  const thead = el('thead');
  thead.appendChild(tr);
  const tbody = el('tbody');
  for (const l of linhas) {
    const r = el('tr');
    for (const c of l) {
      const td = el('td');
      if (typeof c === 'string') td.textContent = c;
      else td.appendChild(c);
      r.appendChild(td);
    }
    tbody.appendChild(r);
  }
  t.append(thead, tbody);
  box.appendChild(t);
  return box;
}

export function criarVistaRelatorio(ctx: ContextoApp): Vista {
  const root = el('section', 'relatorio');

  const cabeca = el('header', 'rel-cabeca');
  cabeca.append(
    el('h1', undefined, 'Relatório da turma'),
    el(
      'p',
      undefined,
      'No fim do percurso, cada aluno copia um código de resultado e o envia a você. Cole aqui os códigos de todos: ' +
        'o relatório mostra onde a turma erra, com o nome de cada erro, e — com respostas suficientes — calibra a ' +
        'dificuldade dos itens.',
    ),
  );

  const fonte = el('div', 'rel-fonte');
  const rotCodigos = el('label', 'rel-rotulo');
  rotCodigos.append(document.createTextNode('Códigos de resultado dos alunos'));
  const codigos = el('textarea', 'rel-codigos');
  codigos.rows = 4;
  codigos.placeholder = 'R1.… (um por linha, ou separados por espaço)';
  rotCodigos.appendChild(codigos);
  const linhaAcoes = el('div', 'rel-acoes');
  const rotCheck = el('label', 'rel-check');
  const check = el('input');
  check.type = 'checkbox';
  check.checked = true;
  rotCheck.append(check, document.createTextNode(' Incluir as respostas deste navegador'));
  const atualizar = el('button', 'app-btn', 'Atualizar relatório');
  atualizar.type = 'button';
  // Sem dados, esta é a tela mais abstrata do app. A turma de exemplo (doze alunos
  // simulados sobre o percurso de demonstração) existe para ela abrir mostrando o que
  // faz — o mapa de calor de erros e a calibração.
  const exemplo = el('button', 'app-btn app-btn-fantasma', 'Carregar turma de exemplo');
  exemplo.type = 'button';
  exemplo.addEventListener('click', () => {
    codigos.value = TURMA_DE_EXEMPLO.join('\n');
    check.checked = false;
    gerar();
    ctx.avisar(
      `Turma de exemplo carregada: ${conta(TURMA_DE_EXEMPLO.length, 'aluno')}, ${conta(DEMO.length, 'questao')}.`,
    );
  });
  linhaAcoes.append(rotCheck, exemplo, atualizar);
  fonte.append(rotCodigos, linhaAcoes);

  const saida = el('div', 'rel-saida');
  root.append(cabeca, fonte, saida);

  function gerar(): void {
    const { pacotes, invalidos, repetidos } = lerCodigos(codigos.value);
    const fontes = [...pacotes];
    if (check.checked) {
      const h = carregarHistorico();
      if (h.length > 0) fontes.push({ nome: 'Este navegador', tentativas: h });
    }
    // Nomes repetidos viram pessoas distintas: "Ana", "Ana (2)".
    const usados = new Map<string, number>();
    const linhas: Linha[] = [];
    for (const f of fontes) {
      const k = (usados.get(f.nome) ?? 0) + 1;
      usados.set(f.nome, k);
      const pessoa = k === 1 ? f.nome : `${f.nome} (${k})`;
      for (const t of f.tentativas) if (hasTemplate(t.templateId)) linhas.push({ pessoa, t });
    }

    saida.replaceChildren();
    if (invalidos > 0) {
      saida.appendChild(
        el(
          'p',
          'rel-aviso',
          `${conta(invalidos, 'trecho').adj('colado')} não ${verbo('ser.pres', numeroDe(invalidos))} código de resultado e ${verbo('ficar.pret', numeroDe(invalidos))} de fora.`,
        ),
      );
    }
    if (repetidos > 0) {
      saida.appendChild(
        el(
          'p',
          'rel-aviso',
          `${conta(repetidos, 'codigo').adj('repetido')} ${verbo('ficar.pret', numeroDe(repetidos))} de fora: cada código conta uma vez só.`,
        ),
      );
    }
    if (linhas.length === 0) {
      saida.appendChild(
        el(
          'p',
          'rel-vazio',
          'Ainda não há respostas. Cole os códigos dos alunos, ou responda questões no percurso deste navegador.',
        ),
      );
      return;
    }

    const pessoas = new Set(linhas.map((l) => l.pessoa));
    const itens = [...new Set(linhas.map((l) => l.t.templateId))].sort(
      (a, b) => (ORDEM.get(a) ?? 0) - (ORDEM.get(b) ?? 0),
    );
    saida.appendChild(
      el(
        'p',
        'rel-resumo',
        `${conta(pessoas.size, 'aluno')} · ${conta(linhas.length, 'resposta')} · ${conta(itens.length, 'item')}`,
      ),
    );

    // --- 1. por item ---------------------------------------------------------
    saida.appendChild(el('h2', 'app-secao', 'Por item'));
    saida.appendChild(
      tabela(
        [
          'Item',
          'Respostas',
          'Acerto',
          'Exigência prevista',
          'Dicas por resposta',
          'Erros mais comuns',
        ],
        itens.map((id) => {
          const ls = linhas.filter((l) => l.t.templateId === id);
          const acertos = ls.filter((l) => l.t.correto).length;
          const dicas = ls.reduce((s, l) => s + l.t.dicas, 0) / ls.length;
          const porErro = new Map<string, number>();
          for (const l of ls) if (l.t.erro) porErro.set(l.t.erro, (porErro.get(l.t.erro) ?? 0) + 1);
          const top = [...porErro.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([c, n]) => `${rotuloErro(c)} (${n}×)`)
            .join('; ');
          const b = bAPriori(id);
          const exigencia = el('span', 'rel-faixa', faixaDeB(b));
          exigencia.title = `b̂ a priori: ${logit(b)} logit`;
          return [
            nomeCurto(id),
            String(ls.length),
            pct(acertos, ls.length),
            exigencia,
            formatBR(dicas, 1),
            top || '—',
          ];
        }),
      ),
    );

    // --- 2. mapa de calor item × erro ------------------------------------------
    const totalErro = new Map<string, number>();
    for (const l of linhas)
      if (l.t.erro) totalErro.set(l.t.erro, (totalErro.get(l.t.erro) ?? 0) + 1);
    const codigosErro = [...totalErro.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
    if (codigosErro.length > 0) {
      saida.appendChild(el('h2', 'app-secao', 'Onde e como a turma erra'));
      saida.appendChild(
        el(
          'p',
          'rel-nota',
          'Cada coluna é um erro nomeado; cada linha, um item. A casa traz sempre o número de respostas — ' +
            'a cor é reforço, não o dado.',
        ),
      );
      const contagem = new Map<string, number>();
      for (const l of linhas)
        if (l.t.erro)
          contagem.set(
            `${l.t.templateId}|${l.t.erro}`,
            (contagem.get(`${l.t.templateId}|${l.t.erro}`) ?? 0) + 1,
          );
      const maximo = Math.max(...contagem.values());
      const box = el('div', 'rel-rolagem');
      const t = el('table', 'rel-calor');
      const thead = el('thead');
      const tr = el('tr');
      tr.appendChild(el('th', 'rel-calor-canto', 'Item'));
      for (const c of codigosErro) {
        const th = el('th', 'rel-calor-col');
        th.appendChild(el('span', undefined, rotuloErro(c)));
        th.title = ERROR_CATALOG[c]?.description ?? c;
        tr.appendChild(th);
      }
      thead.appendChild(tr);
      const tbody = el('tbody');
      for (const id of itens.filter((i) => codigosErro.some((c) => contagem.has(`${i}|${c}`)))) {
        const r = el('tr');
        r.appendChild(el('th', 'rel-calor-item', nomeCurto(id)));
        const n = linhas.filter((l) => l.t.templateId === id).length;
        for (const c of codigosErro) {
          const k = contagem.get(`${id}|${c}`) ?? 0;
          const td = el(
            'td',
            k > 0 ? 'rel-calor-cel tem' : 'rel-calor-cel',
            k > 0 ? String(k) : '',
          );
          // Cinco degraus, não uma rampa contínua: uma rampa não se legenda, e ninguém
          // consegue dizer se um vermelho qualquer vale 3 ou 4.
          if (k > 0) {
            const grau = Math.min(5, Math.max(1, Math.ceil((5 * k) / maximo)));
            td.dataset.grau = String(grau);
            td.style.setProperty('--grau', String(grau));
            td.title = `${nomeCurto(id)} — ${rotuloErro(c)}: ${k} de ${conta(n, 'resposta')}`;
          }
          r.appendChild(td);
        }
        tbody.appendChild(r);
      }
      t.append(thead, tbody);
      box.appendChild(t);
      saida.appendChild(box);

      // A legenda: os degraus com o intervalo de cada um escrito. Com menos de cinco
      // respostas na casa mais cheia há degraus vazios — com máximo 2, só o 3 e o 5
      // recebem casas —, e eles ficam de fora: antes saíam como "1–0".
      const legenda = el('div', 'rel-calor-legenda');
      legenda.appendChild(el('span', undefined, 'Respostas na casa:'));
      for (let g = 1; g <= 5; g++) {
        const de = Math.floor(((g - 1) * maximo) / 5) + 1;
        const ate = Math.floor((g * maximo) / 5);
        if (ate < de) continue;
        const amostra = el('span', 'rel-calor-amostra', de === ate ? String(de) : `${de}–${ate}`);
        amostra.dataset.grau = String(g);
        amostra.style.setProperty('--grau', String(g));
        legenda.appendChild(amostra);
      }
      saida.appendChild(legenda);
    }

    // --- 3. calibração -----------------------------------------------------------
    saida.appendChild(el('h2', 'app-secao', 'Calibração'));
    const porItem = new Map<string, number>();
    for (const l of linhas) porItem.set(l.t.templateId, (porItem.get(l.t.templateId) ?? 0) + 1);
    const elegiveis = itens.filter((i) => (porItem.get(i) ?? 0) >= MIN_RESPOSTAS_ITEM);
    if (pessoas.size < MIN_ALUNOS_CALIBRACAO || elegiveis.length < 2) {
      saida.appendChild(
        el(
          'p',
          'rel-nota',
          `A calibração precisa de pelo menos ${conta(MIN_ALUNOS_CALIBRACAO, 'aluno')} e ${conta(MIN_RESPOSTAS_ITEM, 'resposta')} por item, em ` +
            `dois itens ou mais. Até aqui: ${conta(pessoas.size, 'aluno')} e ${conta(elegiveis.length, 'item')} ` +
            'com respostas suficientes.',
        ),
      );
      return;
    }
    const cal = calibrarRasch(
      linhas
        .filter((l) => elegiveis.includes(l.t.templateId))
        .map((l) => ({ pessoa: l.pessoa, item: l.t.templateId, correto: l.t.correto })),
    );
    // As duas escalas com a mesma média, para que a comparação seja entre itens.
    const mediaApriori = elegiveis.reduce((s, i) => s + bAPriori(i), 0) / elegiveis.length;
    const mediaCal = cal.itens.reduce((s, x) => s + x.b, 0) / cal.itens.length;
    const calibrados: ItemCalibrado[] = cal.itens
      .map((x) => ({ ...x, b: x.b - mediaCal + mediaApriori }))
      .sort((a, b) => (ORDEM.get(a.item) ?? 0) - (ORDEM.get(b.item) ?? 0));
    // O aluno do meio desta turma é a régua da tradução: "5 em 10" quer dizer que
    // metade dela acertaria este item.
    const theta = thetaMediano(cal.pessoas) - mediaCal + mediaApriori;
    /** Uma célula que diz a mesma coisa duas vezes: em alunos, e em logits. */
    const celula = (b: number, sufixo = ''): HTMLElement => {
      const c = el('span', 'rel-duplo');
      c.appendChild(el('strong', 'rel-emdez', `${emDez(b, theta)} em 10`));
      c.appendChild(el('span', 'rel-logit', `${logit(b)}${sufixo}`));
      return c;
    };
    const linhasCal = calibrados.map((x) => {
      const a = bAPriori(x.item);
      const d = x.b - a;
      const marca = el(
        'span',
        Math.abs(d) > 2 * x.ep ? 'rel-dif destaque' : 'rel-dif',
        `${d > 0 ? '+' : ''}${logit(d)}`,
      );
      return [
        nomeCurto(x.item),
        String(x.n),
        celula(a),
        celula(x.b, ` ± ${formatBR(x.ep, 2)}`),
        marca,
      ];
    });
    saida.appendChild(tabela(['Item', 'Respostas', 'Previsto', 'Medido', 'Diferença'], linhasCal));
    saida.appendChild(
      el(
        'p',
        'rel-nota',
        `Cada "N em 10" é a chance de acerto do aluno mediano desta turma ` +
          `(θ̄ = ${logit(theta)}), pela mesma conta que a calibração usa: dá para conferir a tradução na coluna de ` +
          'acerto, acima. O logit fica ao lado, porque é ele que torna a escala comparável entre turmas. ' +
          'b calibrado pelo modelo de Rasch, ± erro-padrão, na escala do a priori (as duas com a mesma média). ' +
          'Diferença maior que dois erros-padrão fica em destaque: o item saiu mais fácil ou mais difícil do que o ' +
          'envelope previa. Com poucas respostas, o erro-padrão é largo — e a diferença, pouco confiável.',
      ),
    );
    const levar = el('button', 'app-btn-mini', 'Mostrar esta calibração no mapa');
    levar.type = 'button';
    levar.addEventListener('click', () => {
      salvarCalibracao(calibrados);
      ctx.avisar('Calibração salva: a ficha de cada item no mapa mostra o b calibrado.');
    });
    saida.appendChild(levar);
  }

  atualizar.addEventListener('click', gerar);
  check.addEventListener('change', gerar);

  return {
    element: root,
    mostrar() {
      gerar();
    },
  };
}
